import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { useParams, useLocation } from "react-router-dom";
import { getMaterialsByFolder, getMaterialsInstitutionByFolder } from "../helper/apicalls/materials";
import CommonPagination from "../components/common/CommonPagination";
import PlayCircleFilledRoundedIcon from "@mui/icons-material/PlayCircleFilledRounded";
import "../styles/MaterialFolder.scss";
import pdfImg from "../assets/images/pdfIcon.svg";
import TooltipComponent from "../components/tooltipComponent";
import audioIcon from "../assets/png/audioIcon.png";
import imgIcon from "../assets/png/imgIcon.png";
import powerpointIcon from "../assets/png/powerpointIcon.png";
import musicIcon from "../assets/png/musicIcon.png";

const VideoIcon = () => (
  <div
    style={{
      width: 50,
      height: 60,
      backgroundColor: "#E5E5E5",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: 8,
    }}
  >
    <PlayCircleFilledRoundedIcon
      style={{ color: "var(--icon-yellow-color)", fontSize: 36 }}
    />
  </div>
);

const PdfIcon = () => (
  <div
    style={{
      width: 60,
      height: 60,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: 8,
    }}
  >
    <img
      src={pdfImg}
      alt="PDF"
      style={{
        width: "100%",
        height: "100%",
        objectFit: "contain",
      }}
    />
  </div>
);

const MaterialsFolder = () => {
  const { folderid } = useParams();
  const location = useLocation();
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeMaterial, setActiveMaterial] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [audioError, setAudioError] = useState(false);
  const [pptViewer, setPptViewer] = useState("office");

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const limit = 10;

  useEffect(() => {
    setPage(1);
  }, [folderid, location.search]);

  useEffect(() => {
    const fetchMaterials = async () => {
      if (!folderid) return;
      try {
        setLoading(true);
        const searchParams = new URLSearchParams(location.search);
        const isInstitution = searchParams.get("type") === "institution";

        const res = isInstitution
          ? await getMaterialsInstitutionByFolder({ folderId: folderid, page, limit })
          : await getMaterialsByFolder({ folderId: folderid, page, limit });

        const items = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
            ? res
            : [];
        if (res?.folder_name) {
          Cookies.set("currentFolderName", res.folder_name);
        }

        setMaterials(items);
        const calculatedTotal = res?.total_pages || (res?.total && res?.limit ? Math.ceil(res.total / res.limit) : 1);
        setTotalPages(calculatedTotal);
        setTotalItems(res?.total || items.length);
      } catch (e) {
        console.error("Failed to load materials for folder", e);
        setMaterials([]);
        setTotalPages(1);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    };

    fetchMaterials();
  }, [folderid, location.search, page]);

  const getMaterialType = (material) => {
    const t = String(material.materials_type || "").toLowerCase();
    if (t === "video" || t === "videos") return "video";
    if (t === "pdf") return "pdf";
    if (t === "audio") return "audio";
    if (t === "ppt") return "ppt";
    if (t === "image") return "image";
    return "";
  };

  const getMaterialLink = (material) => {
    return material?.content || "";
  };

  const handleOpenMaterial = (material) => {
    setActiveMaterial(material);
  };

  const handleCloseModal = () => {
    setActiveMaterial(null);
  };

  const renderMaterialIcon = (item) => {
    const type = (item?.materials_type || "").toLowerCase();
    if (type === "pdf") {
      return <PdfIcon />;
    }
    if (type === "video" || type === "videos") {
      return <VideoIcon />;
    }
    if (type === "audio") {
      return (
        <img
          src={audioIcon}
          alt="Audio"
          style={{ width: "48px", height: "48px", marginTop: "4px" }}
        />
      );
    }
    if (type === "img" || type === "image" || type === "images") {
      return (
        <img
          src={imgIcon}
          alt="Preview"
          style={{ width: "48px", height: "48px", marginTop: "4px" }}
        />
      );
    }
    if (type === "powerpoint" || type === "ppt" || type === "pptx") {
      return (
        <img
          src={powerpointIcon}
          alt="PowerPoint"
          style={{ width: "48px", height: "48px", marginTop: "4px" }}
        />
      );
    }
  };

  return (
    <div className="materials-page">
      {loading ? (
        <div className="materials-loading">Loading materials...</div>
      ) : materials.length === 0 ? (
        <div className="material-folder-empty">
          No materials found in this folder.
        </div>
      ) : (
        <>
          <div className="material-folder-grid">
            {materials.map((material) => (
              <div
                key={material.id || material._id}
                className="material-folder-item"
                onClick={() => handleOpenMaterial(material)}
              >
                {renderMaterialIcon(material)}

                <TooltipComponent className="folder-name">
                  {material.name || material.title || "Untitled"}
                </TooltipComponent>
              </div>
            ))}
          </div>

          <CommonPagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            totalItems={totalItems}
            currentCount={materials.length}
            itemLabel="Materials"
          />
        </>
      )}
      {activeMaterial && (
        <div className="materials-modal" onClick={handleCloseModal}>
          <div
            className="materials-modal__content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="materials-modal__close-wrapper">
              <div className="materials-title">{activeMaterial.name}</div>
              <button
                type="button"
                className="materials-modal__close"
                onClick={handleCloseModal}
              >
                ×
              </button>
            </div>
            {(() => {
              const type = getMaterialType(activeMaterial);
              const link = getMaterialLink(activeMaterial);
              const name = activeMaterial.name || activeMaterial.title || "";

              const isVideo = type === "video";
              const isPdf = type === "pdf";
              const isAudio = type === "audio" || type === "audios";
              const isImage = type === "image" || type === "images";
              const isPpt =
                type === "ppt" || type === "pptx" || type === "presentation";

              const isYouTubeLink =
                typeof link === "string" &&
                (link.includes("youtube.com") || link.includes("youtu.be"));

              let youtubeEmbedUrl = null;
              if (isYouTubeLink) {
                try {
                  if (link.includes("youtube.com/watch")) {
                    const url = new URL(link);
                    const v = url.searchParams.get("v");
                    if (v) {
                      youtubeEmbedUrl = `https://www.youtube.com/embed/${v}`;
                    }
                  } else if (link.includes("youtu.be/")) {
                    const id = link.split("youtu.be/")[1]?.split(/[?&#]/)[0];
                    if (id) {
                      youtubeEmbedUrl = `https://www.youtube.com/embed/${id}`;
                    }
                  }
                } catch (e) {
                  youtubeEmbedUrl = null;
                }
              }

              return (
                <div className="materials-modal__body">
                  {isVideo && link && youtubeEmbedUrl && (
                    <iframe
                      title={name || "Video"}
                      src={youtubeEmbedUrl}
                      style={{
                        width: "100%",
                        height: "70dvh",
                        border: "none",
                      }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  )}

                  {isVideo && link && !youtubeEmbedUrl && (
                    <video
                      controls
                      style={{
                        width: "100%",
                        maxHeight: "70dvh",
                        borderRadius: 8,
                      }}
                    >
                      <source src={link} />
                      Your browser does not support the video tag.
                    </video>
                  )}

                  {isPdf && link && link.toLowerCase().includes(".pdf") && (
                    <iframe
                      title={name || "PDF"}
                      src={link}
                      style={{
                        width: "100%",
                        height: "70dvh",
                        border: "none",
                      }}
                    />
                  )}

                  {isPdf && link && !link.toLowerCase().includes(".pdf") && (
                    <div>
                      <p>This material is an invalid link.</p>
                    </div>
                  )}

                  {isAudio && link && !audioError && (
                    <>
                      <img
                        src={musicIcon}
                        alt="Audio Icon"
                        style={{
                          width: "250px",
                          height: "250px",
                          marginTop: "4px",
                          alignSelf: "center",
                        }}
                      />
                      <audio
                        controls
                        style={{ width: "100%", marginTop: "20px" }}
                        onError={() => setAudioError(true)}
                      >
                        <source src={link} />
                        Your browser does not support the audio element.
                      </audio>
                    </>
                  )}

                  {isAudio && audioError && (
                    <div style={{ textAlign: "center", padding: "20px" }}>
                      <p>
                        Unable to play audio. The file might not be accessible.
                      </p>
                      <a
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "inline-block",
                          marginTop: "10px",
                          padding: "10px 20px",
                          backgroundColor: "#007bff",
                          color: "white",
                          textDecoration: "none",
                          borderRadius: "4px",
                        }}
                      >
                        Download Audio File
                      </a>
                    </div>
                  )}

                  {isImage && link && !imageError && (
                    <img
                      src={link}
                      alt={name || "Image"}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "70dvh",
                        objectFit: "contain",
                      }}
                      onError={() => setImageError(true)}
                    />
                  )}

                  {isImage && imageError && (
                    <div style={{ textAlign: "center", padding: "20px" }}>
                      <p>
                        Unable to load image. The file might not be accessible.
                      </p>
                      <a
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "inline-block",
                          marginTop: "10px",
                          padding: "10px 20px",
                          backgroundColor: "#007bff",
                          color: "white",
                          textDecoration: "none",
                        }}
                      >
                        Open Image in New Tab
                      </a>
                    </div>
                  )}

                  {isPpt && link && (
                    <iframe
                      title={name || "Presentation"}
                      src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                        link,
                      )}`}
                      style={{
                        width: "100%",
                        height: "70dvh",
                        border: "none",
                      }}
                      onError={() => setPptViewer("google")}
                    />
                  )}

                  {pptViewer === "google" && link && (
                    <iframe
                      title={name || "Presentation"}
                      src={`https://docs.google.com/gview?url=${encodeURIComponent(
                        link,
                      )}&embedded=true`}
                      style={{
                        width: "100%",
                        height: "70dvh",
                        border: "none",
                      }}
                      onError={() => setPptViewer("failed")}
                    />
                  )}

                  {pptViewer === "failed" && (
                    <div style={{ textAlign: "center", padding: "20px" }}>
                      <p>
                        <strong>{name || "Presentation"}</strong>
                      </p>
                      <p>Preview not available for this presentation.</p>
                      <a
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "inline-block",
                          marginTop: "10px",
                          padding: "10px 20px",
                          backgroundColor: "#007bff",
                          color: "white",
                          textDecoration: "none",
                          borderRadius: "4px",
                        }}
                      >
                        Download Presentation
                      </a>
                    </div>
                  )}

                  {!link && (
                    <div>No content URL configured for this material.</div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialsFolder;
